import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Camera,
  Video,
  Play,
  Image as ImageIcon,
  X,
  RefreshCw,
} from "lucide-react";
import svgPaths from "../../imports/svg-k6062vvkrz.ts";
import { readSrIdFromUnknown } from "../../utils/srIdDisplay.ts";
import {
  AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  AI_DETECTION_ROWS_PER_PAGE_OPTIONS,
} from "../../constants/aiDetectionPagination";
import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TablePagination from "@mui/material/TablePagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Camera360 } from "../camera360/ui/Camera360";
import type { Camera360Props } from "../camera360/types";
import {
  normalizeCameraAiConfigResponseBody,
  type CameraAiConfigItem,
} from "./AIModelsTab.tsx";
import { ResetFiltersButton } from "../../common/ResetFiltersButton.tsx";
declare const process: { env: Record<string, string | undefined> };
const API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/** IANA id for `X-User-Timezone` (e.g. Asia/Kolkata). */
function getUserTimezoneForApi(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (typeof tz === "string" && tz.trim()) {
      const cleanTZ = tz.trim();
       // Normalize legacy timezone
      if (cleanTZ === "Asia/Calcutta") {
        return "Asia/Kolkata";
      }

      return cleanTZ;
    }

    return "UTC";
  } catch {
    return "UTC";
  }
}

/** Shared headers for GET /detections/dashboard and /detections (date filters use client TZ). */
function detectionsApiHeaders(): Record<string, string> {
  const tz = getUserTimezoneForApi();
  return {
    Accept: "application/json",
    "X-User-Timezone": tz,
  };
}

type SeverityKey = "critical" | "high" | "medium" | "low";

/** GET /detections/dashboard — tile from API */
interface DetectionDashboardTile {
  severity: string;
  total: number;
  use_cases?: Array<{
    use_case_id?: string;
    use_case_name?: string | null;
    count: number;
  }>;
}

interface DetectionDashboardData {
  tiles: DetectionDashboardTile[];
  grand_total?: number;
}

// Types
interface UseCase {
  name: string;
  count: number;
  severity: SeverityKey;
  use_case_id?: string;
}

/** Map API severity string to dashboard card key. */
function normalizeDashboardSeverity(sev: string): SeverityKey | null {
  const u = String(sev).trim().toUpperCase();
  if (u === "CRITICAL" || u === "8-10 CRITICAL") return "critical";
  if (u === "HIGH" || u === "5-8 HIGH") return "high";
  if (u === "MEDIUM" || u === "3-5 MEDIUM") return "medium";
  if (u === "LOW" || u === "1-3 LOW") return "low";
  return null;
}

/** Presets mapped to GET /detections/dashboard `time_frame` and GET /detections `start_date`/`end_date`. */
export type DetectionDashboardDatePreset =
  | "all"
  | "today"
  | "last7"
  | "last30"
  | "this_month";

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Maps UI preset to GET /detections/dashboard `time_frame` (today, last_7_days, …, all_time). */
function applyTileTimeFrameToParams(
  params: URLSearchParams,
  preset: DetectionDashboardDatePreset,
): void {
  if (preset === "all") {
    params.set("time_frame", "all_time");
    return;
  }
  const map: Record<Exclude<DetectionDashboardDatePreset, "all">, string> = {
    today: "today",
    last7: "last_7_days",
    last30: "last_30_days",
    this_month: "this_month",
  };
  params.set("time_frame", map[preset]);
}

/** Maps UI preset to GET /detections `start_date` / `end_date` (YYYY-MM-DD, client local calendar). */
function applyDetectionsDateRangeToParams(
  params: URLSearchParams,
  preset: DetectionDashboardDatePreset,
): void {
  if (preset === "all") return;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = formatLocalYmd(today);
  let startDate: Date;
  switch (preset) {
    case "today":
      startDate = new Date(today);
      break;
    case "last7":
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 6);
      break;
    case "last30":
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 29);
      break;
    case "this_month":
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    default:
      return;
  }
  params.set("start_date", formatLocalYmd(startDate));
  params.set("end_date", end);
}

const DETECTION_DATE_PRESET_OPTIONS: {
  value: DetectionDashboardDatePreset;
  label: string;
}[] = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
];

/** GET /detections — paginated rows for a dashboard tile selection. */
interface DetectionTileApiItem {
  id?: string;
  sr_id?: string | number;
  camera_id?: string;
  camera_name?: string;
  use_case_id?: string;
  use_case_name?: string;
  zone_id?: string;
  zone_name?: string;
  detected_at?: string;
  /** Per-detection payloads; items may be objects or JSON strings. */
  detections?: unknown[];
  photo_url?: string;
  thumbnail_url?: string;
  image_url?: string;
  snapshot_url?: string;
  video_url?: string;
  /** Live stream URL (not the detection recording clip) */
  stream_url?: string;
  live_stream_url?: string;
  live_url?: string;
  severity?: string;
  deleted_at?: string | null;
  /** Workspace lifecycle: active | acknowledged | closed */
  status?: string;
  notification_status?: string;
  notified_to?: string;
  notified_by?: string;
}

interface DetectionTileListBody {
  items?: DetectionTileApiItem[];
  total?: number;
  page?: number;
  page_size?: number;
}

function parseTileListPayload(body: unknown): DetectionTileListBody | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const root = (obj.data ?? obj) as Record<string, unknown>;
  const items = root.items;
  if (!Array.isArray(items)) return null;
  return {
    items: items.filter(
      (i): i is DetectionTileApiItem => i != null && typeof i === "object",
    ),
    total:
      typeof root.total === "number" ? root.total : Number(root.total) || 0,
    page: typeof root.page === "number" ? root.page : Number(root.page) || 1,
    page_size:
      typeof root.page_size === "number"
        ? root.page_size
        : Number(root.page_size) || AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  };
}

function formatDetectedAt(iso: string | undefined): string {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** GET /detections `status` query values. */
type DetectionStatusApiFilter = "active" | "acknowledged" | "closed";

type DetectionTableStatusFilter = "all" | DetectionStatusApiFilter;

const DETECTION_STATUS_FILTER_OPTIONS: {
  value: DetectionTableStatusFilter;
  label: string;
}[] = [
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "acknowledged", label: "Acknowledged" },
];

function detectionStatusFilterLabel(
  filter: DetectionTableStatusFilter,
): string | null {
  if (filter === "all") return null;
  return (
    DETECTION_STATUS_FILTER_OPTIONS.find((o) => o.value === filter)?.label ??
    null
  );
}

function mapApiStatusToTableStatus(
  status: string | undefined,
  notificationStatus?: string | undefined,
): Detection["status"] {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "active" || s.includes("open")) return "active";
  if (s === "acknowledged" || s.includes("ack")) return "acknowledged";
  if (s === "closed" || s.includes("close") || s.includes("resolved"))
    return "closed";
  if (!notificationStatus || typeof notificationStatus !== "string")
    return "active";
  const u = notificationStatus.trim().toLowerCase();
  if (u.includes("closed") || u === "close") return "closed";
  if (u.includes("progress") || u.includes("ack")) return "acknowledged";
  if (u.includes("open") || u === "active") return "active";
  return "active";
}

function detectionStatusListStyle(status: Detection["status"]): {
  backgroundColor: string;
  color: string;
} {
  switch (status) {
    case "closed":
      return { backgroundColor: "#d4edda", color: "#155724" };
    case "acknowledged":
      return { backgroundColor: "#fff3cd", color: "#856404" };
    default:
      return { backgroundColor: "#f8d7da", color: "#721c24" };
  }
}

function detectionStatusGridStyle(status: Detection["status"]): {
  bgcolor: string;
  color: string;
} {
  switch (status) {
    case "closed":
      return { bgcolor: "rgba(40, 167, 69, 0.92)", color: "#fff" };
    case "acknowledged":
      return { bgcolor: "rgba(255, 193, 7, 0.92)", color: "#5c4a00" };
    default:
      return { bgcolor: "rgba(255, 179, 128, 0.92)", color: "#fff" };
  }
}

function parseNotifiedBy(raw: string | undefined): string[] {
  if (raw == null || raw === "") return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    /* not JSON */
  }
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function firstNonEmptyTrimmedString(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return "";
}

function isAnprUseCaseName(name: string): boolean {
  return /\banpr\b/i.test((name || "").trim());
}

function isFaceRecognitionUseCaseName(name: string): boolean {
  const n = (name || "").trim().toLowerCase();
  return (
    /face\s*recogni/.test(n) ||
    /facial\s*recogni/.test(n) ||
    /face\s*recog/.test(n)
  );
}

function looksLikeImageUrlString(s: string): boolean {
  const t = s.trim();
  if (!t) return false;
  if (t.startsWith("data:image/")) return true;
  if (/^https?:\/\//i.test(t)) return true;
  if (t.startsWith("/") && /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(t))
    return true;
  return false;
}

function normalizeTileDetectionsList(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw;
}

function normalizeTileDetectionEntry(
  entry: unknown,
): { metadata?: Record<string, unknown> } | null {
  let obj: Record<string, unknown> | null = null;
  if (entry == null) return null;
  if (typeof entry === "string") {
    try {
      const p = JSON.parse(entry) as unknown;
      if (p && typeof p === "object" && !Array.isArray(p)) {
        obj = p as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  } else if (typeof entry === "object" && !Array.isArray(entry)) {
    obj = entry as Record<string, unknown>;
  }
  if (!obj) return null;
  let metadata = obj.metadata;
  if (typeof metadata === "string") {
    try {
      metadata = JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      metadata = undefined;
    }
  }
  if (metadata != null && typeof metadata !== "object") metadata = undefined;
  return { metadata: metadata as Record<string, unknown> | undefined };
}

/** ANPR: human-readable metadata (e.g. plate_text); skips image-like URL fields. */
function extractAnprMetadataPlainText(detections: unknown[]): string {
  const parts: string[] = [];
  for (const d of detections) {
    const n = normalizeTileDetectionEntry(d);
    const m = n?.metadata;
    if (!m) continue;
    const plate = m.plate_text ?? m.plateText;
    if (typeof plate === "string" && plate.trim()) {
      parts.push(plate.trim());
      continue;
    }
    for (const [k, v] of Object.entries(m)) {
      if (v == null || typeof v === "object") continue;
      const str = String(v).trim();
      if (!str || looksLikeImageUrlString(str)) continue;
      parts.push(`${k}: ${str}`);
    }
  }
  const uniq = [...new Set(parts)];
  return uniq.length > 0 ? uniq.join(", ") : "—";
}

const FACE_METADATA_IMAGE_KEYS = [
  "face_image",
  "face_image_url",
  "image_url",
  "crop_image",
  "thumbnail_url",
  "photo_url",
  "image",
  "face_thumbnail",
  "cropped_face_url",
  "face_crop_url",
  "embedding_image_url",
];

/** Face recognition: first image URL found in detection metadata. */
function extractFaceMetadataImageUrl(detections: unknown[]): string {
  for (const d of detections) {
    const n = normalizeTileDetectionEntry(d);
    const m = n?.metadata;
    if (!m || typeof m !== "object") continue;
    for (const key of FACE_METADATA_IMAGE_KEYS) {
      const v = m[key];
      if (typeof v === "string" && looksLikeImageUrlString(v)) {
        return v.trim();
      }
    }
    for (const v of Object.values(m)) {
      if (typeof v === "string" && looksLikeImageUrlString(v)) {
        return v.trim();
      }
    }
  }
  return "";
}

function mapTileItemToDetection(
  row: DetectionTileApiItem,
  alertDisplaySeq: number,
): Detection {
  const videoUrl = typeof row.video_url === "string" ? row.video_url : "";
  const photoUrl = firstNonEmptyTrimmedString(
    row.photo_url,
    row.thumbnail_url,
    row.image_url,
    row.snapshot_url,
  );
  const thumbnailOnly =
    typeof row.thumbnail_url === "string" ? row.thumbnail_url.trim() : "";
  /** Live-only: never fall back to recording `video_url` (zone column uses this). */
  const liveStreamUrl = firstNonEmptyTrimmedString(
    row.stream_url,
    row.live_stream_url,
    row.live_url,
  );
  /** Camera-column live preview: prefer dedicated live URL, else clip. */
  const liveStream = liveStreamUrl || videoUrl;
  const cameraId =
    row.camera_id != null && String(row.camera_id).trim() !== ""
      ? String(row.camera_id).trim()
      : undefined;
  const zoneId =
    row.zone_id != null && String(row.zone_id).trim() !== ""
      ? String(row.zone_id).trim()
      : undefined;
  const useCaseLabel =
    row.use_case_name != null && String(row.use_case_name).trim() !== ""
      ? String(row.use_case_name)
      : "—";
  const detArr = normalizeTileDetectionsList(row.detections);
  let detectionDataText: string | undefined;
  let detectionDataImageUrl: string | undefined;
  if (isAnprUseCaseName(useCaseLabel)) {
    detectionDataText = extractAnprMetadataPlainText(detArr);
  } else if (isFaceRecognitionUseCaseName(useCaseLabel)) {
    const u = extractFaceMetadataImageUrl(detArr);
    if (u) detectionDataImageUrl = u;
  }
  return {
    id: row.id != null && String(row.id).trim() !== "" ? String(row.id) : "—",
    srId: readSrIdFromUnknown(row),
    dateTime: formatDetectedAt(row.detected_at),
    detectionSource: videoUrl || photoUrl || "—",
    camera:
      row.camera_name != null && String(row.camera_name).trim() !== ""
        ? String(row.camera_name)
        : "—",
    zone:
      row.zone_name != null && String(row.zone_name).trim() !== ""
        ? String(row.zone_name)
        : "—",
    cameraId,
    zoneId,
    status: mapApiStatusToTableStatus(row.status, row.notification_status),
    notifiedTo:
      row.notified_to != null && String(row.notified_to).trim() !== ""
        ? String(row.notified_to)
        : "—",
    notifiedBy: parseNotifiedBy(row.notified_by),
    /** Video clip URL only (play control); `photoUrl` is separate. */
    videoClip: videoUrl,
    photoUrl,
    thumbnailUrl: thumbnailOnly || undefined,
    liveStreamUrl,
    liveStream,
    useCase: useCaseLabel,
    detectedAtMs: detectionDetectedAtMs(row),
    alertDisplaySeq,
    detectionDataText,
    detectionDataImageUrl,
  };
}

/** Fixed order: all four severity cards are always shown in the overview row. */
const DASHBOARD_CARD_SEVERITY_ORDER: SeverityKey[] = [
  "critical",
  "high",
  "medium",
  "low",
];

/** Gap (px) between severity cards in the overview row. */
const SEVERITY_CARD_ROW_GAP_PX = 16;
/** Gaps between four slots (three gaps). Used so each card keeps “1/4 row” width when fewer than four cards show. */
const SEVERITY_CARD_ROW_GAPS_FOR_FOUR = 3;

/** List area is always at least this many rows tall (empty or fewer rows still keep height). */
const SEVERITY_CARD_USE_CASE_MIN_VISIBLE = 4;
/** Beyond this many rows the list stops growing and scrolls (5 visible, 6th+ scrolls). */
const SEVERITY_CARD_USE_CASE_MAX_VISIBLE = 5;
/** Approx. one row: vertical padding + line-height (px). */
const SEVERITY_CARD_USE_CASE_ROW_APPROX_PX = 34;
/** Gap between use case rows in the card (matches `gap: 4`). */
const SEVERITY_CARD_USE_CASE_GAP_PX = 4;
const SEVERITY_CARD_USE_CASE_LIST_MIN_HEIGHT_PX =
  SEVERITY_CARD_USE_CASE_MIN_VISIBLE * SEVERITY_CARD_USE_CASE_ROW_APPROX_PX +
  (SEVERITY_CARD_USE_CASE_MIN_VISIBLE - 1) * SEVERITY_CARD_USE_CASE_GAP_PX;
const SEVERITY_CARD_USE_CASE_LIST_MAX_HEIGHT_PX =
  SEVERITY_CARD_USE_CASE_MAX_VISIBLE * SEVERITY_CARD_USE_CASE_ROW_APPROX_PX +
  (SEVERITY_CARD_USE_CASE_MAX_VISIBLE - 1) * SEVERITY_CARD_USE_CASE_GAP_PX;

function emptySeverityMaps(): {
  useCasesBySeverity: Record<SeverityKey, UseCase[]>;
  totalsBySeverity: Record<SeverityKey, number>;
} {
  const keys: SeverityKey[] = ["critical", "high", "medium", "low"];
  const useCasesBySeverity = {} as Record<SeverityKey, UseCase[]>;
  const totalsBySeverity = {} as Record<SeverityKey, number>;
  for (const k of keys) {
    useCasesBySeverity[k] = [];
    totalsBySeverity[k] = 0;
  }
  return { useCasesBySeverity, totalsBySeverity };
}

function parseDashboardPayload(body: unknown): DetectionDashboardData | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const data = (obj.data ?? obj) as Record<string, unknown>;
  const tiles = data.tiles;
  if (!Array.isArray(tiles)) return null;
  const grand_total =
    typeof data.grand_total === "number" ? data.grand_total : undefined;
  return {
    tiles: tiles.filter(
      (t): t is DetectionDashboardTile =>
        t != null && typeof t === "object" && "severity" in t,
    ),
    grand_total,
  };
}

function applyTilesToMaps(tiles: DetectionDashboardTile[]): {
  useCasesBySeverity: Record<SeverityKey, UseCase[]>;
  totalsBySeverity: Record<SeverityKey, number>;
} {
  const { useCasesBySeverity, totalsBySeverity } = emptySeverityMaps();
  for (const tile of tiles) {
    const key = normalizeDashboardSeverity(tile.severity);
    if (!key) continue;
    const rawList = Array.isArray(tile.use_cases) ? tile.use_cases : [];
    const list: UseCase[] = rawList.map((uc) => ({
      name:
        uc.use_case_name != null && String(uc.use_case_name).trim() !== ""
          ? String(uc.use_case_name).trim()
          : "—",
      count: typeof uc.count === "number" ? uc.count : Number(uc.count) || 0,
      severity: key,
      use_case_id: uc.use_case_id,
    }));
    useCasesBySeverity[key] = list;
    const totalNum =
      typeof tile.total === "number" ? tile.total : Number(tile.total) || 0;
    totalsBySeverity[key] = totalNum;
  }
  return { useCasesBySeverity, totalsBySeverity };
}

interface Detection {
  id: string;
  /** Backend `sr_id` — shown in Alert ID column when present */
  srId?: string;
  dateTime: string;
  detectionSource: string;
  camera: string;
  zone: string;
  /** Tile API `camera_id` — used for drill-down filter query. */
  cameraId?: string;
  /** Tile API `zone_id` — used for drill-down filter query. */
  zoneId?: string;
  status: DetectionStatusApiFilter;
  notifiedTo: string;
  notifiedBy: string[];
  /** `video_url` from API — used for play control */
  videoClip: string;
  /** `photo_url` from API — snapshot for image control */
  photoUrl: string;
  /** API `thumbnail_url` when present (may differ from full snapshot for overlay preview). */
  thumbnailUrl?: string;
  /** Live stream only (API `stream_url` / `live_stream_url` / `live_url`) — not the detection clip. */
  liveStreamUrl: string;
  /** Camera live button: `liveStreamUrl` or clip `video_url` as fallback. */
  liveStream: string;
  useCase: string;
  /** `Date.parse(detected_at)` for list sort; 0 if missing. */
  detectedAtMs: number;
  /** 1-based sequence within current API page (for ALT id + alert column sort). */
  alertDisplaySeq: number;
  /** ANPR rows: plain text from `detections[].metadata` (e.g. plate_text). */
  detectionDataText?: string;
  /** Face recognition: image URL from `detections[].metadata`. */
  detectionDataImageUrl?: string;
}

/** List table column keys for client-side sort (current page only). */
type DetectionListSortColumn = "alertId" | "dateTime" | "camera" | "zone";

function detectionDetectedAtMs(row: DetectionTileApiItem): number {
  const iso = row.detected_at;
  if (!iso || typeof iso !== "string") return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

function compareDetectionsForListSort(
  a: Detection,
  b: Detection,
  column: DetectionListSortColumn,
): number {
  switch (column) {
    case "alertId":
      return a.alertDisplaySeq - b.alertDisplaySeq;
    case "dateTime":
      return (a.detectedAtMs || 0) - (b.detectedAtMs || 0);
    case "camera":
      return a.camera.localeCompare(b.camera, undefined, {
        sensitivity: "base",
      });
    case "zone":
      return a.zone.localeCompare(b.zone, undefined, { sensitivity: "base" });
    default:
      return 0;
  }
}

/** Table display: ALT + numeric suffix (e.g. ALT1001). Uses row order on the current page. */
function formatAlertDisplayId(
  rawId: string | undefined,
  tileItems: Detection[],
  detection: Detection,
  currentPage: number,
  rowsPerPage: number,
  rowIndexInFiltered: number,
): string {
  const sr = detection.srId?.trim();
  if (sr) return sr;
  const raw = rawId?.trim();
  if (raw && /^ALT\d+$/i.test(raw)) {
    const n = raw.replace(/^ALT/i, "");
    return `ALT${n}`;
  }
  if (detection.alertDisplaySeq > 0) {
    return `ALT${1000 + detection.alertDisplaySeq}`;
  }
  const idxOnPage = tileItems.findIndex((d) => d.id === detection.id);
  const seq =
    idxOnPage >= 0
      ? (currentPage - 1) * rowsPerPage + idxOnPage + 1
      : (currentPage - 1) * rowsPerPage + rowIndexInFiltered + 1;
  return `ALT${1000 + seq}`;
}

function DetectionsListSortChevron({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc";
}) {
  if (!active) return null;
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ marginLeft: 2, flexShrink: 0 }}
      aria-hidden
    >
      {direction === "asc" ? (
        <path d="M18 15l-6-6-6 6" />
      ) : (
        <path d="M6 9l6 6 6-6" />
      )}
    </svg>
  );
}

/** Date preset control for Vision AI header and alert-details toolbar (shared state). */
function VisionAiDateRangeSelect({
  value,
  onChange,
  variant,
}: {
  value: DetectionDashboardDatePreset;
  onChange: (preset: DetectionDashboardDatePreset) => void;
  variant: "header" | "toolbar";
}) {
  const isHeader = variant === "header";
  const isDefaultPreset = value === "today";
  const showClear = !isDefaultPreset;
  const bgColor = isHeader
    ? isDefaultPreset
      ? "#f0f0f0"
      : "#fff4e6"
    : isDefaultPreset
      ? "#fff"
      : "#fff4e6";
  const borderColor = isHeader
    ? isDefaultPreset
      ? "#e0e0e0"
      : "#e8c4a0"
    : isDefaultPreset
      ? "#e5e7eb"
      : "#e8c4a0";
  const selectPaddingRight = isHeader
    ? showClear
      ? 76
      : 40
    : showClear
      ? 68
      : 28;

  return (
    <div style={{ position: "relative" }}>
      <span
        style={{
          position: "absolute",
          left: isHeader ? 12 : 10,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
        }}
        aria-hidden
      >
        <svg width="16" height="16" viewBox="0 0 15 16.5" fill="none">
          <path d={svgPaths.p25b8fc00} fill="#4A5565" />
        </svg>
      </span>
      <select
        aria-label="Date range"
        title="Filter detections by date range"
        value={value}
        onChange={(e) =>
          onChange(e.target.value as DetectionDashboardDatePreset)
        }
        style={{
          backgroundColor: bgColor,
          height: isHeader ? 41 : 36,
          minWidth: isHeader ? 178 : 158,
          borderRadius: 10,
          border: `1px solid ${borderColor}`,
          paddingLeft: isHeader ? 36 : 32,
          paddingRight: selectPaddingRight,
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          color: "#1e3a5f",
          cursor: "pointer",
          appearance: "none",
          WebkitAppearance: "none",
        }}
      >
        {DETECTION_DATE_PRESET_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {showClear && (
        <button
          type="button"
          aria-label="Reset date range to Today"
          title="Reset to Today"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange("today");
          }}
          style={{
            position: "absolute",
            right: isHeader ? 38 : 32,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            padding: 0,
            margin: 0,
            border: "none",
            borderRadius: 6,
            background: "transparent",
            color: "#4a5565",
            cursor: "pointer",
          }}
        >
          <X size={isHeader ? 17 : 16} strokeWidth={2.25} aria-hidden />
        </button>
      )}
      <span
        style={{
          position: "absolute",
          right: isHeader ? 12 : 8,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        <ChevronDown
          size={isHeader ? 20 : 18}
          style={{ color: "#4a5565" }}
          aria-hidden
        />
      </span>
    </div>
  );
}

// Severity Card – screenshot design: inline styles only. Rows slightly darker than card; selected = lighter + white vertical bar on left.
const SeverityCard = ({
  severity,
  total,
  useCases,
  selectedUseCase,
  onUseCaseClick,
  /** When true, cards share row equally (all four visible). When false, each card keeps one-quarter row width so the row is not stretched full width. */
  fillRowEqually,
}: {
  severity: string;
  /** Detection count for this severity; `0` when none. */
  total: number;
  useCases: UseCase[];
  selectedUseCase: string | null;
  onUseCaseClick: (useCase: UseCase) => void;
  fillRowEqually: boolean;
}) => {
  const config: Record<
    string,
    {
      bgColor: string;
      rowBgColor: string;
      rowSelectedBg: string;
      title: string;
    }
  > = {
    critical: {
      bgColor: "#8b0000",
      rowBgColor: "#6b0000",
      rowSelectedBg: "#a52a2a",
      title: "CRITICAL ALERTS",
    },
    high: {
      bgColor: "#c62828",
      rowBgColor: "#b71c1c",
      rowSelectedBg: "#d32f2f",
      title: "HIGH PRIORITY",
    },
    medium: {
      bgColor: "#ef5350",
      rowBgColor: "#e53935",
      rowSelectedBg: "#f44336",
      title: "MEDIUM PRIORITY",
    },
    low: {
      bgColor: "#FF8F8F",
      rowBgColor: "#FF7474",
      rowSelectedBg: "#ffccbc",
      title: "LOW PRIORITY",
    },
  };
  const cfg = config[severity] ?? config.critical;

  const quarterRowBasis = `calc((100% - ${SEVERITY_CARD_ROW_GAPS_FOR_FOUR * SEVERITY_CARD_ROW_GAP_PX}px) / 4)`;

  return (
    <div
      style={{
        ...(fillRowEqually
          ? { flex: 1, minWidth: 240 }
          : {
              flex: `0 0 ${quarterRowBasis}`,
              minWidth: 240,
              maxWidth: quarterRowBasis,
            }),
        borderRadius: 16,
        backgroundColor: cfg.bgColor,
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow:
          "0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          padding: 14,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {/* Title top-left, total top-right */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <p
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "20px",
              letterSpacing: "0.3px",
              textTransform: "uppercase",
              color: "#fff",
              margin: 0,
            }}
          >
            {cfg.title}
          </p>
          <p
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 800,
              fontSize: 28,
              lineHeight: "32px",
              color: "#fff",
              margin: 0,
              minHeight: 32,
            }}
          >
            {total}
          </p>
        </div>
        {/* Alert rows: selected item gets white border 1px 1px 1px 3px (all cards) */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: SEVERITY_CARD_USE_CASE_GAP_PX,
            minHeight: SEVERITY_CARD_USE_CASE_LIST_MIN_HEIGHT_PX,
            maxHeight: SEVERITY_CARD_USE_CASE_LIST_MAX_HEIGHT_PX,
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight:
              useCases.length > SEVERITY_CARD_USE_CASE_MAX_VISIBLE ? 4 : 0,
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.45) rgba(0,0,0,0.15)",
          }}
        >
          {useCases.map((uc, index) => {
            const isSelected = selectedUseCase === uc.name;
            return (
              <button
                key={
                  uc.use_case_id
                    ? `${uc.use_case_id}-${index}`
                    : `${uc.name}-${index}`
                }
                type="button"
                onClick={() => onUseCaseClick(uc)}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  padding: "8px 10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: cfg.rowBgColor,
                  ...(isSelected
                    ? {
                        borderWidth: "1px 1px 1px 3px",
                        borderStyle: "solid",
                        borderColor: "#FFFFFF",
                      }
                    : { border: "none" }),
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 500,
                    fontSize: 13,
                    lineHeight: "18px",
                    color: "#fff",
                  }}
                >
                  {uc.name}
                </span>
                <span
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: "20px",
                    color: "#fff",
                  }}
                >
                  {uc.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/** Turn relative API paths into absolute URLs for <video> / <img>. */
function resolveDetectionMediaUrl(url: string): string {
  const u = (url || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = API_BASE.replace(/\/$/, "");
  if (!base) return u;
  if (u.startsWith("/")) return `${base}${u}`;
  return `${base}/${u}`;
}

function isImageMediaUrl(url: string): boolean {
  const path = url.split("?")[0].toLowerCase();
  if (/\/photo(\/|$)/.test(path)) return true;
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/.test(path);
}

/**
 * List table: fixed width for the “Detection Data” column so switching ANPR tiles
 * does not widen the column from the longest row (which looked like a growing gap before Camera).
 * Pair with `tableLayout: "fixed"` on the list `<table>`.
 */
const DETECTION_DATA_LIST_COL_PX = 180;

function FaceDetectionMetadataImage({
  rawUrl,
  variant = "list",
}: {
  rawUrl: string;
  /** Grid cards use a larger crop for readability. */
  variant?: "list" | "grid";
}) {
  const [failed, setFailed] = useState(false);
  const src = rawUrl.trim() ? resolveDetectionMediaUrl(rawUrl.trim()) : "";
  useEffect(() => {
    setFailed(false);
  }, [rawUrl]);
  const maxW = variant === "grid" ? 104 : 80;
  const maxH = variant === "grid" ? 76 : 64;
  if (!src || failed) {
    return (
      <span
        style={{
          fontFamily: "Roboto, sans-serif",
          fontSize: variant === "grid" ? 15 : 14,
          color: "#9E9E9E",
        }}
      >
        —
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      style={{
        maxWidth: maxW,
        maxHeight: maxH,
        width: "auto",
        height: "auto",
        objectFit: "cover",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        display: "block",
      }}
      onError={() => setFailed(true)}
    />
  );
}

function DetectionDataTableCell({
  detection,
  mode,
}: {
  detection: Detection;
  mode: "anpr" | "face";
}) {
  if (mode === "anpr") {
    const t = detection.detectionDataText ?? "—";
    return (
      <td
        style={{
          padding: "8px 12px",
          border: "none",
          width: DETECTION_DATA_LIST_COL_PX,
          maxWidth: DETECTION_DATA_LIST_COL_PX,
          boxSizing: "border-box",
          verticalAlign: "middle",
        }}
      >
        <span
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 14,
            color: "#212121",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {t}
        </span>
      </td>
    );
  }
  const raw = detection.detectionDataImageUrl?.trim() ?? "";
  return (
    <td
      style={{
        padding: "8px 12px",
        border: "none",
        width: DETECTION_DATA_LIST_COL_PX,
        maxWidth: DETECTION_DATA_LIST_COL_PX,
        boxSizing: "border-box",
        verticalAlign: "middle",
      }}
    >
      <FaceDetectionMetadataImage rawUrl={raw} />
    </td>
  );
}

/** Table cell: snapshot thumbnail; opens modal on click. Falls back to icon if the image fails to load. */
function DetectionSourcePhotoButton({
  photo,
  hasVideo,
  onClick,
}: {
  photo: string;
  hasVideo: boolean;
  onClick: () => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const thumbSrc = resolveDetectionMediaUrl(photo);

  useEffect(() => {
    setImgFailed(false);
  }, [photo]);

  const showImg = Boolean(thumbSrc && !imgFailed);

  return (
    <button
      type="button"
      onClick={onClick}
      title={hasVideo ? "View snapshot and recording" : "View snapshot"}
      aria-label={
        hasVideo
          ? "View detection snapshot and recording"
          : "View detection snapshot image"
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 56,
        height: 36,
        padding: 0,
        backgroundColor: "#fff",
        borderRadius: 4,
        border: "1px solid #2932E5",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {showImg ? (
        <img
          src={thumbSrc}
          alt=""
          onError={() => setImgFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
          }}
        />
      ) : (
        <ImageIcon size={16} style={{ color: "#2932E5" }} aria-hidden />
      )}
    </button>
  );
}

/** Grid card snapshot area: resolves API-relative URLs; falls back to icon on error. */
function DetectionGridCardThumbnail({
  photoRaw,
  badge,
}: {
  photoRaw: string;
  badge: React.ReactNode;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const thumbSrc = photoRaw.trim()
    ? resolveDetectionMediaUrl(photoRaw.trim())
    : "";
  useEffect(() => {
    setImgFailed(false);
  }, [photoRaw]);
  const showImg = Boolean(thumbSrc && !imgFailed);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight: { xs: "120px", sm: "140px" },
        height: { xs: "120px", sm: "140px" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgb(33, 33, 33)",
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {showImg ? (
        <Box
          component="img"
          src={thumbSrc}
          alt=""
          onError={() => setImgFailed(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
          }}
        />
      ) : (
        <Video
          size={40}
          style={{ color: "rgba(255,255,255,0.35)" }}
          aria-hidden
        />
      )}
      {badge}
    </Box>
  );
}

/** Map a resolved playback URL to {@link Camera360} `source` (HLS / WebRTC / RTSP-WebRTC / MP4 VOD). */
function buildCamera360Source(resolvedUrl: string): Camera360Props["source"] {
  const u = resolvedUrl.trim();
  const L = u.toLowerCase();
  if (L.startsWith("rtsp://") || L.startsWith("rtsps://")) {
    return { live: { type: "rtsp-webrtc", url: u, rtspUrl: u } };
  }
  if (L.includes(".m3u8") || L.includes("/hls")) {
    return { live: { type: "hls", playlistUrl: u } };
  }
  if (L.startsWith("ws://") || L.startsWith("wss://")) {
    return { live: { type: "webrtc", url: u } };
  }
  return { vod: { type: "mp4", url: u } };
}

/** Snapshot or recording slide for the detection clip carousel in {@link DetectionRecordingModal}. */
export type DetectionCarouselSlide =
  | { kind: "image"; url: string }
  | { kind: "video"; url: string };

/**
 * Ordered carousel: main `photoUrl`, then `thumbnail_url` if it differs, then optional clip.
 * Returns null if fewer than two slides (modal uses single-image layout).
 */
function buildDetectionClipCarouselSlides(
  detection: Detection,
): DetectionCarouselSlide[] | null {
  const mainPhoto = (detection.photoUrl || "").trim();
  const thumbRaw = (detection.thumbnailUrl || "").trim();
  const video = (detection.videoClip || "").trim();

  const slides: DetectionCarouselSlide[] = [];
  if (mainPhoto) slides.push({ kind: "image", url: mainPhoto });

  const mainR = mainPhoto ? resolveDetectionMediaUrl(mainPhoto) : "";
  const thumbR = thumbRaw ? resolveDetectionMediaUrl(thumbRaw) : "";
  if (thumbRaw && thumbR && thumbR !== mainR) {
    slides.push({ kind: "image", url: thumbRaw });
  }
  if (video) slides.push({ kind: "video", url: video });

  if (slides.length < 2) return null;
  return slides;
}

/**
 * Detection recording popup — same shell as {@link PageSettingsDialog}: fixed dimmed overlay,
 * centered Paper, orange title bar, MUI close button.
 */
function DetectionRecordingModal({
  isOpen,
  onClose,
  videoUrl,
  title,
  type,
  detection,
  useCamera360 = false,
  carouselSlides = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title: string;
  type: "clip" | "live";
  detection: Detection | null;
  /** When true with `type: "live"`, render {@link Camera360} instead of a plain &lt;video&gt;. */
  useCamera360?: boolean;
  /** When length ≥ 2 and `type` is `clip`, snapshot + recording carousel (prev/next, dots). */
  carouselSlides?: DetectionCarouselSlide[] | null;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const resolvedUrl = useMemo(
    () => resolveDetectionMediaUrl(videoUrl),
    [videoUrl],
  );
  const clipCarouselSlides = useMemo(() => {
    if (type !== "clip" || !carouselSlides || carouselSlides.length < 2)
      return null;
    return carouselSlides;
  }, [type, carouselSlides]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [imageLightboxSrc, setImageLightboxSrc] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const clipMediaAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) setImageLightboxSrc(null);
  }, [isOpen]);

  useEffect(() => {
    if (!imageLightboxSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setImageLightboxSrc(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [imageLightboxSrc]);

  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setCarouselIndex(0);
  }, [isOpen, clipCarouselSlides]);

  useEffect(() => {
    if (!clipCarouselSlides) return;
    const slide = clipCarouselSlides[carouselIndex];
    if (slide?.kind !== "video" && videoRef.current) {
      videoRef.current.pause();
    }
  }, [carouselIndex, clipCarouselSlides]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (!clipCarouselSlides || clipCarouselSlides.length < 2) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setCarouselIndex(
          (i) =>
            (i - 1 + clipCarouselSlides.length) % clipCarouselSlides.length,
        );
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setCarouselIndex((i) => (i + 1) % clipCarouselSlides.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, clipCarouselSlides]);

  if (!isOpen) return null;

  const carouselSlide = clipCarouselSlides?.[carouselIndex];
  const carouselResolvedUrl = carouselSlide
    ? resolveDetectionMediaUrl(carouselSlide.url)
    : "";

  const showImage = resolvedUrl && isImageMediaUrl(resolvedUrl);
  const showVideo = resolvedUrl && !showImage;

  const thumbnailOverlayRaw = detection?.thumbnailUrl?.trim() ?? "";
  const thumbnailOverlayResolved = thumbnailOverlayRaw
    ? resolveDetectionMediaUrl(thumbnailOverlayRaw)
    : "";
  const thumbnailUrlSlideIndex =
    clipCarouselSlides && thumbnailOverlayResolved
      ? clipCarouselSlides.findIndex(
          (s) =>
            s.kind === "image" &&
            resolveDetectionMediaUrl(s.url) === thumbnailOverlayResolved,
        )
      : -1;
  /** Single preview in Details card: `thumbnail_url` when it differs from the main modal URL or exists as a carousel slide. */
  const mainModalResolved = resolveDetectionMediaUrl(videoUrl);
  const showDetailsThumbnail =
    Boolean(thumbnailOverlayResolved) &&
    (clipCarouselSlides && clipCarouselSlides.length >= 2
      ? thumbnailUrlSlideIndex >= 0
      : thumbnailOverlayResolved !== mainModalResolved);

  const maxWidthDialog =
    type === "live" && useCamera360 && !isMobile
      ? 960
      : type === "clip"
        ? 920
        : 560;
  const showCamera360Player =
    type === "live" && useCamera360 && Boolean(resolvedUrl);
  const zoneLiveMissing =
    type === "live" && useCamera360 && !resolvedUrl.trim();

  return (
    <>
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        p: isMobile ? 0 : 2,
      }}
      role="presentation"
    >
      <Paper
        elevation={8}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="detection-recording-dialog-title"
        sx={{
          position: "relative",
          width: isMobile ? "100vw" : "90vw",
          maxWidth: isMobile ? "100vw" : maxWidthDialog,
          height: isMobile ? "100vh" : "auto",
          maxHeight: isMobile ? "100vh" : "90vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#FFFFFF",
          borderRadius: isMobile ? 0 : 2,
          overflow: "hidden",
        }}
      >
        {/* Header — matches PageSettingsDialog */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
            borderBottom: "1px solid #E5E7EB",
            flexShrink: 0,
            gap: 1,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            {type === "clip" ? (
              <Video size={22} color="#FF8555" aria-hidden />
            ) : (
              <Camera size={22} color="#FF8555" aria-hidden />
            )}
            <Typography
              id="detection-recording-dialog-title"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 20,
                fontWeight: 500,
                color: "#FF8555",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close dialog"
            sx={{
              color: "#4A5565",
              flexShrink: 0,
              "&:hover": {
                color: "#2932E5",
                bgcolor: "rgba(41, 50, 229, 0.08)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Scrollable body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            ref={type === "clip" ? clipMediaAreaRef : undefined}
            sx={{
              bgcolor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: showCamera360Player || zoneLiveMissing ? 360 : 200,
              maxHeight: showCamera360Player
                ? { xs: "70vh", sm: "72vh" }
                : zoneLiveMissing
                  ? 360
                  : { xs: 360, sm: 480 },
              flexShrink: 0,
            }}
          >
            {zoneLiveMissing ? (
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 15,
                  fontWeight: 500,
                  color: "#e5e7eb",
                  px: 3,
                  py: 2,
                  textAlign: "center",
                  maxWidth: 400,
                }}
              >
                No live streaming present. Add a live stream URL for this
                detection (e.g. stream_url); recording clips are not shown here.
              </Typography>
            ) : clipCarouselSlides && carouselSlide ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  minHeight: { xs: 280, sm: 400 },
                  maxHeight: { xs: 360, sm: 480 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: { xs: 5, sm: 6 },
                  py: 1,
                }}
              >
                <IconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCarouselIndex(
                      (i) =>
                        (i - 1 + clipCarouselSlides.length) %
                        clipCarouselSlides.length,
                    );
                  }}
                  aria-label="Previous slide"
                  sx={{
                    position: "absolute",
                    left: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                  }}
                  size="small"
                >
                  <ChevronLeft size={22} aria-hidden />
                </IconButton>
                {!carouselResolvedUrl ? (
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      color: "#9ca3af",
                      px: 2,
                      textAlign: "center",
                    }}
                  >
                    No media URL for this slide.
                  </Typography>
                ) : carouselSlide.kind === "image" ? (
                  <Box
                    component="img"
                    src={carouselResolvedUrl}
                    alt=""
                    role="button"
                    tabIndex={0}
                    title="Click to enlarge"
                    onClick={() => setImageLightboxSrc(carouselResolvedUrl)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setImageLightboxSrc(carouselResolvedUrl);
                      }
                    }}
                    sx={{
                      width: "100%",
                      maxWidth: 820,
                      maxHeight: { xs: 340, sm: 460 },
                      height: "auto",
                      objectFit: "contain",
                      cursor: "zoom-in",
                      transition: "opacity 0.15s ease",
                      "&:hover": { opacity: 0.92 },
                    }}
                  />
                ) : (
                  <Box
                    component="video"
                    ref={videoRef}
                    key={carouselResolvedUrl}
                    src={carouselResolvedUrl}
                    controls
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    sx={{
                      width: "100%",
                      maxHeight: { xs: 220, sm: 300 },
                      objectFit: "contain",
                    }}
                  />
                )}
                <IconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCarouselIndex(
                      (i) => (i + 1) % clipCarouselSlides.length,
                    );
                  }}
                  aria-label="Next slide"
                  sx={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                  }}
                  size="small"
                >
                  <ChevronRight size={22} aria-hidden />
                </IconButton>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 0.75,
                    zIndex: 2,
                  }}
                >
                  {clipCarouselSlides.map((_, i) => (
                    <Box
                      key={`dot-${i}`}
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarouselIndex(i);
                      }}
                      aria-label={`Go to slide ${i + 1}`}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        border: "none",
                        p: 0,
                        cursor: "pointer",
                        bgcolor:
                          i === carouselIndex
                            ? "#fff"
                            : "rgba(255,255,255,0.35)",
                        "&:hover": {
                          bgcolor:
                            i === carouselIndex
                              ? "#fff"
                              : "rgba(255,255,255,0.55)",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ) : !resolvedUrl ? (
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  color: "#9ca3af",
                  px: 2,
                  textAlign: "center",
                }}
              >
                No video or image URL for this detection.
              </Typography>
            ) : showCamera360Player ? (
              <Box
                sx={{
                  width: "100%",
                  minHeight: { xs: 320, sm: 400 },
                  maxHeight: { xs: "65vh", sm: "68vh" },
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Camera360
                  key={resolvedUrl}
                  cameraId={`dashboard-live-${detection?.id ?? "row"}`}
                  cameraType="bullet"
                  cameraConfigType="bullet"
                  source={buildCamera360Source(resolvedUrl)}
                  ui={{
                    theme: "dark",
                    controls: {
                      timeline: false,
                      ptz: false,
                    },
                    miniMap: { enabled: false },
                    pip: { enabled: true },
                  }}
                  initialView={{ mode: "bubble" }}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 360,
                  }}
                />
              </Box>
            ) : showImage ? (
              <Box
                component="img"
                src={resolvedUrl}
                alt=""
                role="button"
                tabIndex={0}
                title="Click to enlarge"
                onClick={() => setImageLightboxSrc(resolvedUrl)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setImageLightboxSrc(resolvedUrl);
                  }
                }}
                sx={{
                  maxWidth: "100%",
                  maxHeight: { xs: 360, sm: 480 },
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  cursor: "zoom-in",
                  transition: "opacity 0.15s ease",
                  "&:hover": { opacity: 0.92 },
                }}
              />
            ) : showVideo ? (
              <Box
                component="video"
                ref={videoRef}
                key={resolvedUrl}
                src={resolvedUrl}
                controls
                autoPlay
                muted
                playsInline
                preload="metadata"
                sx={{
                  width: "100%",
                  maxHeight: { xs: 240, sm: 320 },
                  objectFit: "contain",
                }}
              />
            ) : null}
          </Box>

          {detection && (
            <Box
              sx={{
                position: "relative",
                p: 3,
                pr: showDetailsThumbnail ? { xs: 2, sm: 15 } : 3,
                pb: showDetailsThumbnail ? { xs: 2, sm: 2.5 } : 3,
                bgcolor: "#fafafa",
                borderTop: "1px solid #E5E7EB",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 12,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                  mb: 1,
                }}
              >
                Details
              </Typography>
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: "grid",
                  gridTemplateColumns: "88px 1fr",
                  gap: 0.5,
                  rowGap: 0.75,
                }}
              >
                {[
                  ["When", detection.dateTime],
                  ["Camera", detection.camera],
                  ["Zone", detection.zone],
                  ["Status", detection.status],
                ].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography
                      component="dt"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 13,
                        color: "#6B7280",
                      }}
                    >
                      {k}
                    </Typography>
                    <Typography
                      component="dd"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 13,
                        color: "#212121",
                        m: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {v}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
              {showDetailsThumbnail ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    if (
                      clipCarouselSlides &&
                      clipCarouselSlides.length >= 2 &&
                      thumbnailUrlSlideIndex >= 0
                    ) {
                      setCarouselIndex(thumbnailUrlSlideIndex);
                      requestAnimationFrame(() => {
                        clipMediaAreaRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      });
                    } else {
                      setImageLightboxSrc(thumbnailOverlayResolved);
                    }
                  }}
                  aria-label="Show thumbnail image in viewer"
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    m: 0,
                    p: 0,
                    border:
                      clipCarouselSlides &&
                      clipCarouselSlides.length >= 2 &&
                      carouselIndex === thumbnailUrlSlideIndex
                        ? "2px solid #2932E5"
                        : "2px solid #e5e7eb",
                    cursor: "pointer",
                    borderRadius: "8px",
                    bgcolor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                    lineHeight: 0,
                    "&:focus-visible": {
                      outline: "2px solid #2932E5",
                      outlineOffset: 2,
                    },
                    "&:hover": {
                      borderColor: "#2932E5",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={thumbnailOverlayResolved}
                    alt=""
                    sx={{
                      display: "block",
                      width: { xs: 80, sm: 104 },
                      height: { xs: 56, sm: 72 },
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
    {imageLightboxSrc ? (
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Enlarged image"
        onClick={() => setImageLightboxSrc(null)}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 10050,
          bgcolor: "rgba(0,0,0,0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1, sm: 2 },
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "relative",
            display: "inline-block",
            maxWidth: "min(98vw, 1800px)",
            maxHeight: "min(95vh, 1200px)",
          }}
        >
          <IconButton
            onClick={() => setImageLightboxSrc(null)}
            aria-label="Close enlarged image"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.55)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.72)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Box
            component="img"
            src={imageLightboxSrc}
            alt=""
            sx={{
              display: "block",
              maxWidth: "min(98vw, 1800px)",
              maxHeight: "min(95vh, 1200px)",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
              boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
            }}
          />
        </Box>
      </Box>
    ) : null}
    </>
  );
}

/** Single global selection: only one tile row across all cards; drives GET /detections. */
type SelectedItem = {
  severity: SeverityKey;
  useCase: string;
  useCaseId: string;
};

type ToolbarFilterOption = { id: string; name: string };

function configMatchesUseCase(
  config: CameraAiConfigItem,
  useCaseId: string,
): boolean {
  return (config.use_cases ?? []).some(
    (uc) => !uc.deleted_at && uc.use_case_id === useCaseId,
  );
}

function buildCameraOptionsForUseCase(
  configs: readonly CameraAiConfigItem[],
  nameById: ReadonlyMap<string, string>,
): ToolbarFilterOption[] {
  const byId = new Map<string, string>();
  for (const config of configs) {
    const id = config.camera_id.trim();
    if (!id || byId.has(id)) continue;
    byId.set(id, nameById.get(id) ?? id);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
}

/** Name lookup for configured camera ids only (GET /cameras). */
async function loadCameraNamesById(
  base: string,
  cameraIds: readonly string[],
  signal: AbortSignal,
): Promise<Map<string, string>> {
  const needed = new Set(cameraIds.map((id) => id.trim()).filter(Boolean));
  const byId = new Map<string, string>();
  if (needed.size === 0) return byId;

  const res = await fetch(`${base}/cameras`, {
    method: "GET",
    headers: detectionsApiHeaders(),
    signal,
  });
  const json = (await res.json().catch(() => ({}))) as { data?: unknown };
  if (!res.ok) return byId;

  const list = Array.isArray(json?.data) ? json.data : [];
  for (const raw of list) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as { id?: string; camera_id?: string; name?: string };
    const id = String(row.camera_id ?? row.id ?? "").trim();
    const name = String(row.name ?? "").trim();
    if (!id || !needed.has(id) || !name || byId.has(id)) continue;
    byId.set(id, name);
  }
  return byId;
}

/** GET /cameras/{camera_id}/zones — zones linked to the selected camera. */
async function fetchZonesByCameraId(
  base: string,
  cameraId: string,
  signal: AbortSignal,
): Promise<ToolbarFilterOption[]> {
  const res = await fetch(
    `${base}/cameras/${encodeURIComponent(cameraId)}/zones`,
    {
      method: "GET",
      headers: detectionsApiHeaders(),
      signal,
    },
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return [];

  let list: unknown[] = [];
  if (Array.isArray(body)) list = body;
  else if (body && typeof body === "object") {
    const d = (body as { data?: unknown }).data;
    if (Array.isArray(d)) list = d;
  }

  const out: ToolbarFilterOption[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as {
      id?: string;
      name?: string;
      deleted_at?: string | null;
    };
    const id = row.id != null ? String(row.id).trim() : "";
    if (!id || seen.has(id)) continue;
    if (row.deleted_at != null && String(row.deleted_at).trim() !== "") {
      continue;
    }
    const name = row.name != null ? String(row.name).trim() : "";
    seen.add(id);
    out.push({ id, name: name || id });
  }
  out.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  return out;
}

// Main Dashboard Component
export default function AIDetectionDashboard() {
  const [useCasesBySeverity, setUseCasesBySeverity] = useState<
    Record<SeverityKey, UseCase[]>
  >(() => emptySeverityMaps().useCasesBySeverity);
  const [totalsBySeverity, setTotalsBySeverity] = useState<
    Record<SeverityKey, number>
  >(() => emptySeverityMaps().totalsBySeverity);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  /** Header date → `time_frame` on GET /detections/dashboard (+ `X-User-Timezone`). Changing this also syncs the table toolbar date. */
  const [dashboardDatePreset, setDashboardDatePreset] =
    useState<DetectionDashboardDatePreset>("today");
  /** Table toolbar date: drives GET /detections; independent of the header (header changes copy here, not vice versa). */
  const [tableDatePreset, setTableDatePreset] =
    useState<DetectionDashboardDatePreset>("today");
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  /** Client-side table filter (current API page only). */
  const [tableStatusFilter, setTableStatusFilter] =
    useState<DetectionTableStatusFilter>("all");
  /** Tile API `camera_id`; "" = no camera filter. */
  const [tableCameraIdFilter, setTableCameraIdFilter] = useState("");
  /** Tile API `zone_id`; "" = no zone filter. */
  const [tableZoneIdFilter, setTableZoneIdFilter] = useState("");
  const [tableFilterMenuAnchor, setTableFilterMenuAnchor] =
    useState<null | HTMLElement>(null);
  /** Detections table: list vs grid (default grid — matches Manage Use Case). */
  const [detectionsViewMode, setDetectionsViewMode] = useState<"list" | "grid">(
    "grid",
  );
  const [videoModal, setVideoModal] = useState<{
    isOpen: boolean;
    videoUrl: string;
    title: string;
    type: "clip" | "live";
    /** Set for detection-clip popup (edit/delete); omitted for live camera preview */
    detection: Detection | null;
    /** Zone column live popup uses {@link Camera360}; camera column uses native &lt;video&gt;. */
    useCamera360?: boolean;
    /** Snapshot + recording slides when both exist (carousel in modal). */
    carouselSlides?: DetectionCarouselSlide[] | null;
  }>({
    isOpen: false,
    videoUrl: "",
    title: "",
    type: "clip",
    detection: null,
    useCamera360: false,
    carouselSlides: undefined,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(
    AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  );
  /** Client-side list/grid row order (current page + filters). Null = API order. */
  const [listSort, setListSort] = useState<{
    column: DetectionListSortColumn;
    dir: "asc" | "desc";
  } | null>(null);

  /** Drop sort if a legacy column (removed from UI) is still in state after hot reload. */
  useEffect(() => {
    setListSort((prev) => {
      if (!prev) return prev;
      const allowed: DetectionListSortColumn[] = [
        "alertId",
        "dateTime",
        "camera",
        "zone",
      ];
      return allowed.includes(prev.column) ? prev : null;
    });
  }, []);

  const [tileItems, setTileItems] = useState<Detection[]>([]);
  const [tileTotal, setTileTotal] = useState(0);
  const [tileLoading, setTileLoading] = useState(false);
  const [tileError, setTileError] = useState<string | null>(null);
  const tileFetchAbortRef = useRef<AbortController | null>(null);
  const filterConfigsAbortRef = useRef<AbortController | null>(null);
  const cameraZonesAbortRef = useRef<AbortController | null>(null);
  /** Cameras for the selected use case (from GET /camera-ai-configs?use_case_id=…). */
  const [useCaseCameraOptions, setUseCaseCameraOptions] = useState<
    ToolbarFilterOption[]
  >([]);
  /** Zones from GET /cameras/{camera_id}/zones for the selected camera filter. */
  const [cameraZoneOptions, setCameraZoneOptions] = useState<
    ToolbarFilterOption[]
  >([]);

  const handleDashboardDatePresetChange = useCallback(
    (preset: DetectionDashboardDatePreset) => {
      setDashboardDatePreset(preset);
      setTableDatePreset(preset);
      setCurrentPage(1);
    },
    [],
  );

  const handleTableDatePresetChange = useCallback(
    (preset: DetectionDashboardDatePreset) => {
      setTableDatePreset(preset);
      setCurrentPage(1);
    },
    [],
  );

  const fetchDashboard = useCallback(async () => {
    const base = API_BASE.replace(/\/$/, "");
    if (!base) {
      setDashboardError(
        "API base URL is not configured (REACT_APP_API_URL_FOR_AI_DETECTION).",
      );
      const empty = emptySeverityMaps();
      setUseCasesBySeverity(empty.useCasesBySeverity);
      setTotalsBySeverity(empty.totalsBySeverity);
      return;
    }
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const params = new URLSearchParams();
      applyTileTimeFrameToParams(params, dashboardDatePreset);
      const cam = tableCameraIdFilter.trim();
      if (cam) params.set("camera_id", cam);
      const zn = tableZoneIdFilter.trim();
      if (zn) params.set("zone_id", zn);
      const qs = params.toString();
      const url = `${base}/detections/dashboard${qs ? `?${qs}` : ""}`;
      const res = await fetch(url, {
        method: "GET",
        headers: detectionsApiHeaders(),
      });
      const text = await res.text();
      let body: unknown = {};
      if (text.trim()) {
        try {
          body = JSON.parse(text) as unknown;
        } catch {
          throw new Error("Invalid JSON from detections dashboard");
        }
      }
      if (!res.ok) {
        const detail = (body as { detail?: unknown })?.detail;
        let msg = res.statusText || `HTTP ${res.status}`;
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail)) {
          const parts = detail
            .map((d: { msg?: string }) => d?.msg)
            .filter(Boolean);
          if (parts.length) msg = parts.join("; ");
        }
        throw new Error(msg);
      }
      const parsed = parseDashboardPayload(body);
      if (!parsed) {
        const empty = emptySeverityMaps();
        setUseCasesBySeverity(empty.useCasesBySeverity);
        setTotalsBySeverity(empty.totalsBySeverity);
        return;
      }
      const mapped = applyTilesToMaps(parsed.tiles);
      setUseCasesBySeverity(mapped.useCasesBySeverity);
      setTotalsBySeverity(mapped.totalsBySeverity);
    } catch (e) {
      setDashboardError(
        e instanceof Error ? e.message : "Failed to load dashboard",
      );
      const empty = emptySeverityMaps();
      setUseCasesBySeverity(empty.useCasesBySeverity);
      setTotalsBySeverity(empty.totalsBySeverity);
    } finally {
      setDashboardLoading(false);
    }
  }, [
    dashboardDatePreset,
    tableCameraIdFilter,
    tableZoneIdFilter,
  ]);

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard]);

  const fetchCameraAiConfigsForUseCase = useCallback(async (useCaseId: string) => {
    const base = API_BASE.replace(/\/$/, "");
    const ucId = useCaseId.trim();
    filterConfigsAbortRef.current?.abort();
    const ac = new AbortController();
    filterConfigsAbortRef.current = ac;

    if (!base || !ucId) {
      setUseCaseCameraOptions([]);
      return;
    }

    try {
      const fetchConfigs = async (url: string) => {
        const res = await fetch(url, {
          method: "GET",
          headers: detectionsApiHeaders(),
          signal: ac.signal,
        });
        const raw = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
        return normalizeCameraAiConfigResponseBody(raw).filter((config) =>
          configMatchesUseCase(config, ucId),
        );
      };

      let list: CameraAiConfigItem[];
      try {
        list = await fetchConfigs(
          `${base}/camera-ai-configs?${new URLSearchParams({ use_case_id: ucId }).toString()}`,
        );
      } catch {
        list = await fetchConfigs(`${base}/camera-ai-configs`);
      }
      const cameraIds = [
        ...new Set(
          list.map((c) => c.camera_id.trim()).filter((id) => id.length > 0),
        ),
      ];
      const nameById = await loadCameraNamesById(base, cameraIds, ac.signal);
      if (!ac.signal.aborted) {
        setUseCaseCameraOptions(buildCameraOptionsForUseCase(list, nameById));
      }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!ac.signal.aborted) setUseCaseCameraOptions([]);
    }
  }, []);

  const fetchCameraZonesForFilter = useCallback(async (cameraId: string) => {
    const base = API_BASE.replace(/\/$/, "");
    const camId = cameraId.trim();
    cameraZonesAbortRef.current?.abort();
    const ac = new AbortController();
    cameraZonesAbortRef.current = ac;

    if (!base || !camId) {
      setCameraZoneOptions([]);
      return;
    }

    try {
      const zones = await fetchZonesByCameraId(base, camId, ac.signal);
      if (!ac.signal.aborted) setCameraZoneOptions(zones);
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!ac.signal.aborted) setCameraZoneOptions([]);
    }
  }, []);

  useEffect(() => {
    const useCaseId = selectedItem?.useCaseId?.trim() ?? "";
    if (!useCaseId) {
      setUseCaseCameraOptions([]);
      return;
    }
    void fetchCameraAiConfigsForUseCase(useCaseId);
    return () => {
      filterConfigsAbortRef.current?.abort();
    };
  }, [selectedItem?.useCaseId, fetchCameraAiConfigsForUseCase]);

  useEffect(() => {
    const cameraId = tableCameraIdFilter.trim();
    if (!cameraId) {
      setCameraZoneOptions([]);
      return;
    }
    void fetchCameraZonesForFilter(cameraId);
    return () => {
      cameraZonesAbortRef.current?.abort();
    };
  }, [tableCameraIdFilter, fetchCameraZonesForFilter]);

  const fetchTileList = useCallback(async () => {
    const base = API_BASE.replace(/\/$/, "");
    if (!base) {
      setTileItems([]);
      setTileTotal(0);
      setTileError(null);
      setTileLoading(false);
      return;
    }

    tileFetchAbortRef.current?.abort();
    const ac = new AbortController();
    tileFetchAbortRef.current = ac;

    if (!selectedItem?.useCaseId) {
      setTileItems([]);
      setTileTotal(0);
      setTileLoading(false);
      if (selectedItem && !selectedItem.useCaseId) {
        setTileError("This use case has no id; cannot load detections.");
      } else {
        setTileError(null);
      }
      return;
    }

    setTileLoading(true);
    setTileError(null);
    try {
      const params = new URLSearchParams();
      params.set("use_case_id", selectedItem.useCaseId);
      params.set("page", String(currentPage));
      params.set("page_size", String(rowsPerPage));
      applyDetectionsDateRangeToParams(params, tableDatePreset);
      if (tableCameraIdFilter.trim()) {
        params.set("camera_id", tableCameraIdFilter.trim());
      }
      if (tableZoneIdFilter.trim()) {
        params.set("zone_id", tableZoneIdFilter.trim());
      }
      const q = searchQuery.trim();
      if (q) {
        params.set("search", q.slice(0, 200));
      }
      if (tableStatusFilter !== "all") {
        params.set("status", tableStatusFilter);
      }
      const url = `${base}/detections?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: detectionsApiHeaders(),
        signal: ac.signal,
      });
      const text = await res.text();
      let body: unknown = {};
      if (text.trim()) {
        try {
          body = JSON.parse(text) as unknown;
        } catch {
          throw new Error("Invalid JSON from detections");
        }
      }
      if (!res.ok) {
        const detail = (body as { detail?: unknown })?.detail;
        let msg = res.statusText || `HTTP ${res.status}`;
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail)) {
          const parts = detail
            .map((d: { msg?: string }) => d?.msg)
            .filter(Boolean);
          if (parts.length) msg = parts.join("; ");
        }
        throw new Error(msg);
      }
      const parsed = parseTileListPayload(body);
      if (!parsed) {
        setTileItems([]);
        setTileTotal(0);
        return;
      }
      const rows = (parsed.items ?? []).map((item, idx) =>
        mapTileItemToDetection(item, (currentPage - 1) * rowsPerPage + idx + 1),
      );
      setTileItems(rows);
      setTileTotal(
        typeof parsed.total === "number"
          ? parsed.total
          : Number(parsed.total) || rows.length,
      );
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      const message =
        e instanceof Error ? e.message : "Failed to load tile detections";
      console.error("[AIDetectionDashboard] detections failed", message);
      setTileError(message);
      setTileItems([]);
      setTileTotal(0);
    } finally {
      if (!ac.signal.aborted) setTileLoading(false);
    }
  }, [
    selectedItem,
    tableDatePreset,
    currentPage,
    rowsPerPage,
    tableCameraIdFilter,
    tableZoneIdFilter,
    searchQuery,
    tableStatusFilter,
  ]);

  useEffect(() => {
    void fetchTileList();
    return () => {
      tileFetchAbortRef.current?.abort();
    };
  }, [fetchTileList]);

  const handleVisionAiHeaderRefresh = useCallback(() => {
    void fetchDashboard();
    void fetchTileList();
    const useCaseId = selectedItem?.useCaseId?.trim() ?? "";
    if (useCaseId) void fetchCameraAiConfigsForUseCase(useCaseId);
    const cameraId = tableCameraIdFilter.trim();
    if (cameraId) void fetchCameraZonesForFilter(cameraId);
  }, [
    fetchDashboard,
    fetchTileList,
    fetchCameraAiConfigsForUseCase,
    fetchCameraZonesForFilter,
    selectedItem?.useCaseId,
    tableCameraIdFilter,
  ]);

  useEffect(() => {
    setListSort(null);
  }, [
    selectedItem?.useCaseId,
    selectedItem?.severity,
    tableDatePreset,
    tableStatusFilter,
  ]);

  /** First severity (card order) that has use cases: select its first row so GET /detections runs on load. */
  const defaultSelected = useMemo((): SelectedItem | null => {
    for (const sev of DASHBOARD_CARD_SEVERITY_ORDER) {
      const first = useCasesBySeverity[sev][0];
      if (first) {
        return {
          severity: sev,
          useCase: first.name,
          useCaseId: first.use_case_id != null ? String(first.use_case_id) : "",
        };
      }
    }
    return null;
  }, [useCasesBySeverity]);

  useEffect(() => {
    setSelectedItem((prev) => {
      if (!defaultSelected) return null;
      if (!prev) return defaultSelected;
      const list = useCasesBySeverity[prev.severity];
      const match = list.find((u) => u.name === prev.useCase);
      if (match) {
        const id = match.use_case_id != null ? String(match.use_case_id) : "";
        if (
          prev.severity === match.severity &&
          prev.useCase === match.name &&
          prev.useCaseId === id
        )
          return prev;
        return { severity: prev.severity, useCase: match.name, useCaseId: id };
      }
      return defaultSelected;
    });
  }, [defaultSelected, useCasesBySeverity]);

  /** Drop camera/zone selection when it is not in the configured options for this use case. */
  useEffect(() => {
    if (!tableCameraIdFilter.trim()) return;
    if (!useCaseCameraOptions.some((o) => o.id === tableCameraIdFilter.trim())) {
      setTableCameraIdFilter("");
    }
  }, [tableCameraIdFilter, useCaseCameraOptions]);

  useEffect(() => {
    const zoneId = tableZoneIdFilter.trim();
    if (!zoneId) return;
    if (!cameraZoneOptions.some((o) => o.id === zoneId)) {
      setTableZoneIdFilter("");
    }
  }, [tableZoneIdFilter, cameraZoneOptions]);

  /** Rows from GET /detections (status filter is server-side via `status` query param). */
  const filteredTileRows = tileItems;

  const displayTileRows = useMemo(() => {
    const rows = [...filteredTileRows];
    if (!listSort) return rows;
    const { column, dir } = listSort;
    const mult = dir === "asc" ? 1 : -1;
    rows.sort((a, b) => mult * compareDetectionsForListSort(a, b, column));
    return rows;
  }, [filteredTileRows, listSort]);

  const detectionDataColumnMode = useMemo((): "anpr" | "face" | null => {
    const name = selectedItem?.useCase?.trim() ?? "";
    if (isAnprUseCaseName(name)) return "anpr";
    if (isFaceRecognitionUseCaseName(name)) return "face";
    return null;
  }, [selectedItem?.useCase]);

  /** No overflow scroll when there is nothing to scroll (removes stray scrollbar in empty/filtered-out state). */
  const suppressDetectionsScroll = displayTileRows.length === 0;

  const toggleListSort = useCallback((column: DetectionListSortColumn) => {
    setListSort((prev) => {
      if (!prev || prev.column !== column) {
        return { column, dir: "asc" };
      }
      return { column, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }, []);

  /** Active toolbar + status/search filters (badge on Filter button). */
  const tableActiveFilterCount = useMemo(() => {
    let n = 0;
    if (tableStatusFilter !== "all") n += 1;
    if (searchQuery.trim()) n += 1;
    if (tableCameraIdFilter.trim()) n += 1;
    if (tableZoneIdFilter.trim()) n += 1;
    if (tableDatePreset !== "today") n += 1;
    return n;
  }, [
    tableStatusFilter,
    searchQuery,
    tableCameraIdFilter,
    tableZoneIdFilter,
    tableDatePreset,
  ]);

  const tableStatusFilterLabel = detectionStatusFilterLabel(tableStatusFilter);

  const handleResetFilters = () => {
    setSearchQuery("");
    setTableStatusFilter("all");
    setTableCameraIdFilter("");
    setTableZoneIdFilter("");
    setTableDatePreset("today");
    setDashboardDatePreset("today");
    setTableFilterMenuAnchor(null);
    setCurrentPage(1);
  };

  const handleUseCaseClick = (severity: SeverityKey, uc: UseCase) => {
    setSelectedItem((prev) => {
      if (prev?.severity === severity && prev?.useCase === uc.name) return null;
      return {
        severity,
        useCase: uc.name,
        useCaseId: uc.use_case_id != null ? String(uc.use_case_id) : "",
      };
    });
    setCurrentPage(1);
    setTableCameraIdFilter("");
    setTableZoneIdFilter("");
  };

  const handleDetectionRecordingModalClose = useCallback(() => {
    setVideoModal((prev) => ({
      ...prev,
      isOpen: false,
      detection: null,
      useCamera360: false,
      carouselSlides: undefined,
    }));
  }, []);

  const handleDetectionSourceClick = (detection: Detection) => {
    const src = (detection.videoClip || "").trim();
    if (!src) return;
    const useCaseTitle =
      detection.useCase && detection.useCase !== "—"
        ? detection.useCase
        : `Detection ${detection.id}`;
    setVideoModal({
      isOpen: true,
      videoUrl: src,
      title: useCaseTitle,
      type: "clip",
      detection,
      useCamera360: false,
      carouselSlides: undefined,
    });
  };

  const handleDetectionPhotoClick = (detection: Detection) => {
    const src = (detection.photoUrl || "").trim();
    if (!src) return;
    const useCaseTitle =
      detection.useCase && detection.useCase !== "—"
        ? detection.useCase
        : `Detection ${detection.id}`;
    const carouselSlides = buildDetectionClipCarouselSlides(detection);
    const hasVideoInModal =
      carouselSlides?.some((s) => s.kind === "video") ?? false;
    setVideoModal({
      isOpen: true,
      videoUrl: src,
      title: `${useCaseTitle}${
        hasVideoInModal ? " — Snapshot & recording" : " — Snapshot"
      }`,
      type: "clip",
      detection,
      useCamera360: false,
      carouselSlides: carouselSlides ?? undefined,
    });
  };

  const handleCameraClick = (detection: Detection) => {
    setVideoModal({
      isOpen: true,
      videoUrl: detection.liveStream,
      title: `Live Feed - ${detection.camera}`,
      type: "live",
      detection: null,
      useCamera360: false,
    });
  };

  const handleZoneLiveClick = (detection: Detection) => {
    setVideoModal({
      isOpen: true,
      videoUrl: (detection.liveStreamUrl || "").trim(),
      title: `Live — ${detection.camera} · ${detection.zone}`,
      type: "live",
      detection,
      useCamera360: true,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#fff",
        minHeight: 0,
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      {/* Vision AI Dashboard header – title + session-wide date range */}
      <div
        style={{
          flexShrink: 0,
          backgroundColor: "#fff",
          padding: "5px 20px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
          border: "1px solid #e5e7eb",
          borderBottom: "none",
          borderRadius: "12px 12px 0 0",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h2
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "28px",
              color: "#212121",
              letterSpacing: "-0.5px",
              margin: 0,
            }}
          >
            Vision AI Dashboard
          </h2>
          {dashboardError && (
            <p
              style={{
                margin: 0,
                fontFamily: "Roboto, sans-serif",
                fontSize: 13,
                color: "#c62828",
              }}
            >
              {dashboardError}
            </p>
          )}
          {tileError && !dashboardError && (
            <p
              style={{
                margin: 0,
                fontFamily: "Roboto, sans-serif",
                fontSize: 13,
                color: "#c62828",
              }}
            >
              {tileError}
            </p>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
            paddingTop: 1,
          }}
        >
          <VisionAiDateRangeSelect
            value={dashboardDatePreset}
            onChange={handleDashboardDatePresetChange}
            variant="header"
          />
          <button
            type="button"
            onClick={handleVisionAiHeaderRefresh}
            disabled={dashboardLoading || tileLoading}
            aria-label="Refresh dashboard and detection list"
            title="Refresh dashboard and detection list"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 41,
              height: 41,
              flexShrink: 0,
              borderRadius: 10,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              color: "#4a5565",
              cursor:
                dashboardLoading || tileLoading ? "not-allowed" : "pointer",
              opacity: dashboardLoading || tileLoading ? 0.55 : 1,
              boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            }}
          >
            <RefreshCw size={18} strokeWidth={2.25} aria-hidden />
          </button>
        </div>
      </div>

      {/* Priority Alert Overview Cards – horizontal row */}
      <div
        style={{
          flexShrink: 0,
          padding: "4px",
          backgroundColor: "#fafafa",
          border: "1px solid #e5e7eb",
          borderRadius: "0 0 12px 12px",
          borderTop: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: SEVERITY_CARD_ROW_GAP_PX,
            overflowX: "auto",
            paddingBottom: 4,
            opacity: dashboardLoading ? 0.65 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          {DASHBOARD_CARD_SEVERITY_ORDER.map((sev) => (
            <SeverityCard
              key={sev}
              severity={sev}
              total={totalsBySeverity[sev]}
              useCases={useCasesBySeverity[sev]}
              selectedUseCase={
                selectedItem?.severity === sev ? selectedItem.useCase : null
              }
              onUseCaseClick={(uc) => handleUseCaseClick(sev, uc)}
              fillRowEqually
            />
          ))}
        </div>
      </div>

      {/* Alert Details Table Section – gap above, divider, borders */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: "#fff",
          padding: 8,
          marginTop: 5,
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
        }}
      >
        {/* Row 1: Section title; filter row is below */}
        <div
          style={{
            marginBottom: 2,
            paddingBottom: 2,
          }}
        >
          <p
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              fontSize: 18,
              color: "#212121",
              margin: 0,
            }}
          >
            {selectedItem?.useCase || "All Detections"}
          </p>
        </div>
        {/* Row 2: Search, toggles, Camera, Zone, date (left) | filter chips + Filter (right) — below header border */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 5,
            paddingBottom: 5,
            // borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div style={{ position: "relative" }}>
              <Search
                size={18}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#4a5565",
                }}
              />
              <input
                type="text"
                placeholder="Search ID, Camera, Zone, Notified To, and Notified By"
                title="Search by ID, Camera, Zone, Notified To, and Notified By"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: 280,
                  height: 36,
                  paddingLeft: 36,
                  paddingRight: 12,
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  color: "#212121",
                  backgroundColor: "#fff",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                title="List view"
                onClick={() => setDetectionsViewMode("list")}
                style={{
                  padding: 8,
                  backgroundColor:
                    detectionsViewMode === "list" ? "#2932E5" : "#fff",
                  color: detectionsViewMode === "list" ? "#fff" : "#4a5565",
                  border: "none",
                  borderRight: "1px solid #e5e7eb",
                  cursor: "pointer",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="5" width="18" height="2" fill="currentColor" />
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="2"
                    fill="currentColor"
                  />
                  <rect
                    x="3"
                    y="17"
                    width="18"
                    height="2"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <button
                type="button"
                title="Grid view"
                onClick={() => setDetectionsViewMode("grid")}
                style={{
                  padding: 8,
                  backgroundColor:
                    detectionsViewMode === "grid" ? "#2932E5" : "#fff",
                  color: detectionsViewMode === "grid" ? "#fff" : "#4a5565",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="7" height="7" fill="currentColor" />
                  <rect x="14" y="3" width="7" height="7" fill="currentColor" />
                  <rect x="3" y="14" width="7" height="7" fill="currentColor" />
                  <rect
                    x="14"
                    y="14"
                    width="7"
                    height="7"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <select
              aria-label="Filter by camera"
              title="Filter detections by camera (sent as camera_id to the API)"
              value={tableCameraIdFilter}
              onChange={(e) => {
                setTableCameraIdFilter(e.target.value);
                setTableZoneIdFilter("");
                setCurrentPage(1);
              }}
              style={{
                height: 36,
                minWidth: 140,
                maxWidth: 220,
                padding: "0 10px",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#212121",
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="">All cameras</option>
              {useCaseCameraOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by zone"
              title="Filter detections by zone"
              value={tableZoneIdFilter}
              disabled={!tableCameraIdFilter.trim()}
              onChange={(e) => {
                setTableZoneIdFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: 36,
                minWidth: 120,
                maxWidth: 220,
                padding: "0 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#212121",
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="">All zones</option>
              {cameraZoneOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <VisionAiDateRangeSelect
              value={tableDatePreset}
              onChange={handleTableDatePresetChange}
              variant="toolbar"
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {/* Camera / zone: query params on GET /detections; no duplicate pills here. */}
            {tableStatusFilter !== "all" && tableStatusFilterLabel ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #2932E5",
                  backgroundColor: "#fff",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 13,
                  color: "#2932E5",
                }}
              >
                {tableStatusFilterLabel}
                <button
                  type="button"
                  onClick={() => setTableStatusFilter("all")}
                  aria-label="Remove status filter"
                  style={{
                    border: "none",
                    background: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#2932E5",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ) : null}
            <button
              type="button"
              onClick={(e) => setTableFilterMenuAnchor(e.currentTarget)}
              aria-label={
                tableActiveFilterCount > 0
                  ? `Open table filters, ${tableActiveFilterCount} active`
                  : "Open table filters"
              }
              aria-haspopup="true"
              style={{
                position: "relative",
                height: 36,
                paddingLeft: 14,
                paddingRight: tableActiveFilterCount > 0 ? 30 : 16,
                backgroundColor: "#2932E5",
                color: "#fff",
                border: "1px solid #2932E5",
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                overflow: "visible",
              }}
            >
              <Filter size={18} color="#fff" aria-hidden />
              Filter
              {tableActiveFilterCount > 0 ? (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 6,
                    minWidth: 18,
                    height: 18,
                    padding: "0 4px",
                    borderRadius: 999,
                    backgroundColor: "#E53935",
                    border: "2px solid #fff",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    lineHeight: 1,
                  }}
                >
                  {tableActiveFilterCount}
                </span>
              ) : null}
            </button>
            <ResetFiltersButton
              disabled={tableActiveFilterCount === 0}
              onClick={handleResetFilters}
            />
          </div>
        </div>
        <Menu
          anchorEl={tableFilterMenuAnchor}
          open={Boolean(tableFilterMenuAnchor)}
          onClose={() => setTableFilterMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Typography
            sx={{
              px: 2,
              py: 1,
              fontSize: 12,
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            STATUS
          </Typography>
          {DETECTION_STATUS_FILTER_OPTIONS.map((opt) => (
            <MenuItem
              key={opt.value}
              onClick={() => {
                setTableStatusFilter(opt.value);
                setCurrentPage(1);
                setTableFilterMenuAnchor(null);
              }}
              selected={tableStatusFilter === opt.value}
              sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Table – horizontal divider under header only; no vertical column borders */}
        <div
          style={{
            flex: 1,
            minHeight: 120,
            overflow: suppressDetectionsScroll ? "hidden" : "auto",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            opacity: tileLoading ? 0.65 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          {detectionsViewMode === "list" ? (
            <table
              style={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
                borderSpacing: 0,
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "#F0F0F0",
                }}
              >
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleListSort("alertId")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#2932E5",
                      }}
                    >
                      ID
                      <DetectionsListSortChevron
                        active={listSort?.column === "alertId"}
                        direction={listSort?.dir ?? "asc"}
                      />
                    </button>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleListSort("dateTime")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#2932E5",
                      }}
                    >
                      Date & Time
                      <DetectionsListSortChevron
                        active={listSort?.column === "dateTime"}
                        direction={listSort?.dir ?? "asc"}
                      />
                    </button>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 10px",
                      border: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#2932E5",
                    }}
                  >
                    Detection Source
                  </th>
                  {detectionDataColumnMode ? (
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        border: "none",
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#2932E5",
                        width: DETECTION_DATA_LIST_COL_PX,
                        maxWidth: DETECTION_DATA_LIST_COL_PX,
                        boxSizing: "border-box",
                      }}
                    >
                      Detection Data
                    </th>
                  ) : null}
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleListSort("camera")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#2932E5",
                      }}
                    >
                      <Camera size={16} style={{ flexShrink: 0 }} />
                      Camera
                      <DetectionsListSortChevron
                        active={listSort?.column === "camera"}
                        direction={listSort?.dir ?? "asc"}
                      />
                    </button>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleListSort("zone")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#2932E5",
                      }}
                    >
                      Zone
                      <DetectionsListSortChevron
                        active={listSort?.column === "zone"}
                        direction={listSort?.dir ?? "asc"}
                      />
                    </button>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#2932E5",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#2932E5",
                    }}
                  >
                    Notified to
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#2932E5",
                    }}
                  >
                    Notified By
                  </th>
                </tr>
              </thead>
              <tbody>
                {!tileLoading && !tileError && displayTileRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={detectionDataColumnMode ? 9 : 8}
                      style={{
                        padding: 24,
                        textAlign: "center",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 14,
                        color: "#4a5565",
                        border: "none",
                      }}
                    >
                      No data is available
                    </td>
                  </tr>
                )}
                {displayTileRows.map((detection, rowIndex) => (
                  <tr
                    key={`${detection.id}-${detection.dateTime}-${rowIndex}`}
                    style={{ borderBottom: "1px solid #e5e7eb" }}
                  >
                    <td
                      style={{
                        padding: "8px 8px",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 14,
                        fontWeight: 400,
                        color: "#000000",
                        border: "none",
                      }}
                    >
                      {detection.id === "—"
                        ? "—"
                        : formatAlertDisplayId(
                            detection.id,
                            tileItems,
                            detection,
                            currentPage,
                            rowsPerPage,
                            rowIndex,
                          )}
                    </td>
                    <td style={{ padding: "8px 12px", border: "none" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: "#ffecd4",
                          padding: "4px 4px",
                          borderRadius: 4,
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 14,
                          color: "#212121",
                        }}
                      >
                        {detection.dateTime}
                      </span>
                    </td>
                    <td style={{ padding: "5px 10px", border: "none" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {(() => {
                          const photo = detection.photoUrl?.trim();
                          const video = detection.videoClip?.trim();

                          if (!photo && !video) {
                            return (
                              <span
                                style={{
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: 14,
                                  color: "#9E9E9E",
                                }}
                              >
                                —
                              </span>
                            );
                          }

                          if (photo) {
                            return (
                              <DetectionSourcePhotoButton
                                photo={photo}
                                hasVideo={Boolean(video)}
                                onClick={() =>
                                  handleDetectionPhotoClick(detection)
                                }
                              />
                            );
                          }

                          return (
                            <button
                              type="button"
                              onClick={() =>
                                handleDetectionSourceClick(detection)
                              }
                              title="Play clip"
                              aria-label="Play detection video"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 24,
                                backgroundColor: "#000",
                                borderRadius: 4,
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <Play size={12} style={{ color: "#fff" }} />
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                    {detectionDataColumnMode ? (
                      <DetectionDataTableCell
                        detection={detection}
                        mode={detectionDataColumnMode}
                      />
                    ) : null}
                    <td style={{ padding: "8px 12px", border: "none" }}>
                      <button
                        type="button"
                        // onClick={() => handleCameraClick(detection)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 14,
                          color: "#2932E5",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        <Camera size={16} />
                        {detection.camera}
                      </button>
                    </td>
                    <td style={{ padding: "5px 12px", border: "none" }}>
                      <button
                        type="button"
                        // onClick={() => handleZoneLiveClick(detection)}
                        title="Open live stream for this zone"
                        aria-label={`Open live stream, zone ${detection.zone}`}
                        style={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 14,
                          color: "#2932E5",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          textAlign: "left",
                          // textDecoration: "underline",
                        }}
                      >
                        {detection.zone}
                      </button>
                    </td>
                    {/* <td
                    style={{
                      padding: "8px 12px",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      color: "#212121",
                      border: "none",
                    }}
                  >
                    -
                  </td> */}
                    <td style={{ padding: "8px 12px", border: "none" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: 4,
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 12,
                          ...detectionStatusListStyle(detection.status),
                        }}
                      >
                        {detection.status}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 14,
                        color: "#212121",
                        border: "none",
                      }}
                    >
                      {detection.notifiedTo}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 14,
                        color: "#212121",
                        border: "none",
                      }}
                    >
                      {detection.notifiedBy.length > 0
                        ? detection.notifiedBy.join(", ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: suppressDetectionsScroll ? "hidden" : "auto",
                p: { xs: 0.5, sm: 0 },
              }}
            >
              {!tileLoading && !tileError && displayTileRows.length === 0 && (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#4a5565",
                  }}
                >
                  No data is available
                </Box>
              )}
              <Grid container spacing={1}>
                {displayTileRows.map((detection, rowIndex) => {
                  const displayId =
                    detection.id === "—"
                      ? "—"
                      : formatAlertDisplayId(
                          detection.id,
                          tileItems,
                          detection,
                          currentPage,
                          rowsPerPage,
                          rowIndex,
                        );
                  const photo = detection.photoUrl?.trim();
                  const video = detection.videoClip?.trim();
                  const { bgcolor: statusBg, color: statusColor } =
                    detectionStatusGridStyle(detection.status);
                  const hasMedia = Boolean(photo || video);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2}
                      key={`${detection.id}-${detection.dateTime}-${rowIndex}-grid`}
                      sx={{ display: "flex", minWidth: 0 }}
                    >
                      <Card
                        onClick={() => {
                          if (photo) handleDetectionPhotoClick(detection);
                          else if (video) handleDetectionSourceClick(detection);
                        }}
                        sx={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          minWidth: 0,
                          height: "auto",
                          minHeight: { xs: 280, sm: 300 },
                          bgcolor: "rgb(255, 255, 255)",
                          color: "rgb(33, 33, 33)",
                          boxShadow: "var(--Paper-shadow)",
                          backgroundImage: "var(--Paper-overlay)",
                          overflow: "hidden",
                          borderRadius: "14px",
                          transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: hasMedia ? "pointer" : "default",
                          border: "1px solid rgb(229, 231, 235)",
                          "&:hover": {
                            boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                            transform: "translateY(-4px) scale(1.02)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            paddingLeft: { xs: 1, sm: "10px" },
                            paddingRight: { xs: 1, sm: "10px" },
                            pt: 1,
                            pb: 0,
                            flexShrink: 0,
                          }}
                        >
                          <DetectionGridCardThumbnail
                            photoRaw={photo ?? ""}
                            badge={
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 10,
                                  left: 10,
                                  zIndex: 1,
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: "20px",
                                  bgcolor: statusBg,
                                  color: statusColor,
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  letterSpacing: "0.02em",
                                  textTransform: "uppercase",
                                }}
                              >
                                {detection.status}
                              </Box>
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            gap: 0.875,
                            px: { xs: 1.25, sm: 1.5 },
                            pt: { xs: 1.125, sm: 1.35 },
                            pb: { xs: 1.35, sm: 1.6 },
                            overflow: "visible",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "15px", sm: "17px" },
                              fontWeight: 700,
                              lineHeight: 1.35,
                              color: "#212121",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {displayId === "—" ? "—" : displayId}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "13px", sm: "14px" },
                              lineHeight: 1.45,
                              color: "#4A5565",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {detection.dateTime}
                          </Typography>
                          {detectionDataColumnMode === "anpr" ? (
                            <Typography
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: { xs: "13px", sm: "14px" },
                                lineHeight: 1.5,
                                color: "#212121",
                                minWidth: 0,
                                wordBreak: "break-word",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {detection.detectionDataText ?? "—"}
                            </Typography>
                          ) : null}
                          {detectionDataColumnMode === "face" ? (
                            <Box
                              sx={{
                                minHeight: 56,
                                maxHeight: 84,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                py: 0.35,
                              }}
                            >
                              <FaceDetectionMetadataImage
                                rawUrl={detection.detectionDataImageUrl ?? ""}
                                variant="grid"
                              />
                            </Box>
                          ) : null}
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "13px", sm: "14px" },
                              lineHeight: 1.45,
                              color: "#4A5565",
                              minWidth: 0,
                              wordBreak: "break-word",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {detection.camera} · {detection.zone}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px" },
                              lineHeight: 1.4,
                              fontWeight: 600,
                              color: "#616161",
                              pt: 0.25,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            ID: {displayId}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </div>

        {/* Pagination Footer */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "10px",
            border: "1px solid #e5e7eb",
            borderTop: "none",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            bgcolor: "#F2F2F2",
            minHeight: 52,
            px: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#374151",
              m: 0,
            }}
          >
            Total Items: {tileTotal}
          </Typography>
          <TablePagination
            component="div"
            count={tileTotal}
            page={Math.max(0, currentPage - 1)}
            onPageChange={(_event, newPage) => setCurrentPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setCurrentPage(1);
            }}
            rowsPerPageOptions={[...AI_DETECTION_ROWS_PER_PAGE_OPTIONS]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
            labelRowsPerPage="Rows per page:"
            sx={{
              border: "none",
              "& .MuiTablePagination-toolbar": { minHeight: 52, px: 0 },
              "& .MuiTablePagination-selectLabel": {
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#374151",
              },
              "& .MuiTablePagination-displayedRows": {
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#374151",
                m: 0,
              },
              "& .MuiTablePagination-actions": {
                color: "#4A5565",
              },
            }}
          />
        </Box>
      </div>

      <DetectionRecordingModal
        isOpen={videoModal.isOpen}
        onClose={handleDetectionRecordingModalClose}
        videoUrl={videoModal.videoUrl}
        title={videoModal.title}
        type={videoModal.type}
        detection={videoModal.detection}
        useCamera360={Boolean(videoModal.useCamera360)}
        carouselSlides={videoModal.carouselSlides ?? null}
      />
    </div>
  );
}
